import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    InputLabel,
    Select,
    MenuItem,
    FormControl,
    FormControlLabel,
    Typography,
    Checkbox,
    Divider,
    IconButton
} from '@mui/material';
import { useTheme } from '@mui/system';
import { Add, Delete } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ApiHelper from 'utils/apiHelper';
import { validateNumber, validateAlphaPlus, validateAlphanumeric } from 'utils/commonHelpers';
import AddEnergyRateDialog from '../Management/EnergyRates/AddEnergyRateDialog';

const AddEnergyRuleDialog = ({ onClose, onCloseCancel, ruleType, data, ruleStatementValues }) => {
    const theme = useTheme();
    const [ruleName, setRuleName] = useState('');
    const [ruleDescription, setRuleDescription] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [rateId, setRateId] = useState('');
    const [policyId, setPolicyId] = useState('');
    const [orderValue, setOrderValue] = useState(data.rates.default_order_value);
    const operatorLookupsData = data.rates.operator_lookups;
    const [groupAttributeId, setGroupAttributeId] = useState();
    const [deviceTypeAttributeId, setDeviceTypeAttributeId] = useState();
    const [viewAttributeId, setViewAttributeId] = useState();
    const [disableAddClause, setDisableAddClause] = useState(true);
    const [ruleStatements, setRuleStatements] = useState([
        {
            id: 1,
            group: '',
            attribute: '',
            negative: false,
            operator: '',
            value: '',
            connector: '1',
            disabledConnector: true,
            operatorOptions: data.operators,
            valueTextField: true,
            valueOptions: []
        }
    ]);
    useEffect(() => {
        operatorLookupsData.map((operatorLookupData) => {
            if (operatorLookupData.attrib.attribname === 'groupid') {
                setGroupAttributeId(operatorLookupData.attrib.attribid);
            } else if (operatorLookupData.attrib.attribname === 'devtype') {
                setDeviceTypeAttributeId(operatorLookupData.attrib.attribid);
            } else if (operatorLookupData.attrib.attribname === 'viewid') {
                setViewAttributeId(operatorLookupData.attrib.attribid);
            }
        });
        ruleStatements.map((ruleStatementData) => {
            if (ruleStatementData['attribute'] !== '' && ruleStatementData['operator'] !== '' && ruleStatementData['value'] !== '') {
                setDisableAddClause(false);
            } else {
                setDisableAddClause(true);
            }
        });
    }, [ruleStatements, operatorLookupsData]);

    const handleAddClause = () => {
        const tmp = JSON.parse(JSON.stringify(ruleStatements));
        for (let i = 0; i < tmp.length; i++) {
            tmp[i]['disabledConnector'] = false;
        }
        tmp.push({
            id: ruleStatements.length + 1,
            attribute: '',
            negative: false,
            operator: '',
            value: '',
            connector: '1',
            disabledConnector: true,
            operatorOptions: data.operators,
            valueTextField: true,
            valueOptions: []
        });
        setRuleStatements(tmp);
    };

    const handleDeleteRuleStatement = (key) => {
        const tmp = JSON.parse(JSON.stringify(ruleStatements));
        const index = tmp.findIndex((rule) => rule.id === key);
        tmp.splice(index, 1);
        tmp[tmp.length - 1]['disabledConnector'] = true;
        setRuleStatements(tmp);
    };

    const handleChangeInputRuleStatementData = (e, ruleId) => {
        const tmp = JSON.parse(JSON.stringify(ruleStatements));
        const selectedRule = tmp.filter((rule) => rule.id === ruleId);
        if (e.target.name === 'negative') {
            selectedRule[0][e.target.name] = e.target.checked;
        } else {
            selectedRule[0][e.target.name] = e.target.value;
            const operatorLookup = operatorLookupsData.some((item) => item.attrib.attribid === selectedRule[0]['attribute']);
            if (operatorLookup) {
                selectedRule[0]['operatorOptions'] = [operatorLookupsData[0].theoper];
                if (selectedRule[0]['attribute'] === groupAttributeId) selectedRule[0]['valueOptions'] = ruleStatementValues.group_values;
                else if (selectedRule[0]['attribute'] === deviceTypeAttributeId)
                    selectedRule[0]['valueOptions'] = ruleStatementValues.device_type_values;
                else if (selectedRule[0]['attribute'] === viewAttributeId)
                    selectedRule[0]['valueOptions'] = ruleStatementValues.view_values;
                else selectedRule[0]['valueOptions'] = [{ name: 'Yes' }, { name: 'No' }];
                selectedRule[0]['valueTextField'] = false;
            } else {
                selectedRule[0]['valueTextField'] = true;
                selectedRule[0]['operatorOptions'] = data.operators;
            }
        }
        setRuleStatements(tmp);
    };

    const handleSubmit = () => {
        if (ruleName === '') {
            toast.info('The rule name is required');
            return true;
        }
        if (ruleType === 'energy' && rateId === '') {
            toast.info(`Please select the ${ruleType} value`);
            return true;
        }
        if (!validateAlphanumeric(ruleName)) {
            toast.info('The rule name must be alphanumeric');
            return true;
        }
        if (!validateAlphaPlus(ruleDescription)) {
            toast.info('The rule description must be alphanumeric');
            return true;
        }
        if (orderValue === '') {
            toast.info('Order value is required and it should be an integer');
            return true;
        } else {
            if (validateNumber(orderValue, 'integer') !== true) {
                toast.info('Order value should be an integer');
                return true;
            }
        }

        ApiHelper.post('/api/automation/rules/', {
            rule_name: ruleName,
            rule_description: ruleDescription,
            rule_type: ruleType,
            enabled: enabled,
            order_value: parseInt(orderValue),
            rate_id: rateId,
            policy_id: policyId,
            rule_statements: ruleStatements
        })
            .then(() => {
                toast.success('Successfully added.');
                onClose();
            })
            .catch((err) => {
                toast.error(`Failed! ${err.response.data.message}`);
            });
    };

    return (
        <Dialog open={true} onClose={onCloseCancel}>
            <DialogTitle sx={{ fontSize: '16px' }}>Add Energy Rule</DialogTitle>
            <DialogContent sx={{ minWidth: '900px' }}>
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                sx={{ width: '100%' }}
                                label="Rule Name"
                                value={ruleName}
                                onChange={(e) => setRuleName(e.target.value)}
                                inputProps={{
                                    maxLength: 30
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                sx={{ width: '100%' }}
                                label="Rule Description"
                                value={ruleDescription}
                                onChange={(e) => setRuleDescription(e.target.value)}
                                inputProps={{
                                    maxLength: 50
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>Energy Rate</InputLabel>
                                <Select label="Energy Rate" value={rateId} onChange={(e) => setRateId(e.target.value)}>
                                    {data.rates.energy_data.map((data, key) => (
                                        <MenuItem key={key} value={data.id}>
                                            {(() => {
                                                const date = new Date(data.startdate);
                                                const months = [
                                                    'Jan',
                                                    'Feb',
                                                    'Mar',
                                                    'Apr',
                                                    'May',
                                                    'Jun',
                                                    'Jul',
                                                    'Aug',
                                                    'Sep',
                                                    'Oct',
                                                    'Nov',
                                                    'Dec'
                                                ];
                                                return `${months[date.getMonth()]}-${String(date.getDate()).padStart(
                                                    2,
                                                    '0'
                                                )}-${date.getFullYear()}: ${data.name}`;
                                            })()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl sx={{ width: '100%', mt: 1 }}>
                                <InputLabel>Order</InputLabel>
                                <Select label="Order" value={orderValue} onChange={(e) => setOrderValue(e.target.value)}>
                                    {Array.from({ length: data.rates.default_order_value }, (v, i) => i + 1).map((value, key) => (
                                        <MenuItem key={key} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3} sx={{ mt: 1 }}>
                            <FormControlLabel
                                value="end"
                                sx={{ float: 'right' }}
                                control={<Checkbox checked={!!enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                                label="Enabled"
                                labelPlacement="end"
                            />
                        </Grid>
                    </Grid>
                </Box>
                <Divider sx={{ border: `1px solid ${theme.palette.primary.main}` }} />
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Grid container spacing={1}>
                        {ruleStatements.map((ruleStatementData, ruleStatementKey) => (
                            <React.Fragment key={ruleStatementKey}>
                                <Grid item xs={1}>
                                    {ruleStatementData.id === 1 ? (
                                        <Typography sx={{ padding: 2, fontSize: '16px' }}>IF</Typography>
                                    ) : (
                                        <IconButton
                                            size="medium"
                                            sx={{
                                                float: 'right'
                                            }}
                                            color="error"
                                            onClick={() => handleDeleteRuleStatement(ruleStatementData.id)}
                                        >
                                            <Delete fontSize="medium" />
                                        </IconButton>
                                    )}
                                </Grid>
                                <Grid item xs={3}>
                                    <FormControl sx={{ width: '100%' }}>
                                        <InputLabel>Attribute</InputLabel>
                                        <Select
                                            label="Attribute"
                                            name="attribute"
                                            value={ruleStatementData['attribute']}
                                            onChange={(e) => handleChangeInputRuleStatementData(e, ruleStatementData.id)}
                                        >
                                            {data.rates.attributes.map((attributeData, key) => (
                                                <MenuItem key={key} value={attributeData.attribid}>
                                                    {attributeData.attrib_label ? attributeData.attrib_label : attributeData.attribname}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={1}>
                                    <FormControlLabel
                                        value="top"
                                        control={
                                            <Checkbox
                                                name="negative"
                                                value={ruleStatementData['negative']}
                                                onChange={(e) => handleChangeInputRuleStatementData(e, ruleStatementData.id)}
                                            />
                                        }
                                        label="NOT"
                                        labelPlacement="top"
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <FormControl sx={{ width: '100%' }}>
                                        <InputLabel>Operator</InputLabel>
                                        <Select
                                            label="Operator"
                                            name="operator"
                                            value={ruleStatementData['operator']}
                                            onChange={(e) => handleChangeInputRuleStatementData(e, ruleStatementData.id)}
                                        >
                                            {ruleStatementData['operatorOptions'].map((operatorData, key) => (
                                                <MenuItem key={key} value={operatorData.operatorid}>
                                                    {operatorData.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={3}>
                                    {ruleStatementData['valueTextField'] === false ? (
                                        <FormControl sx={{ width: '100%' }}>
                                            <InputLabel>Value</InputLabel>
                                            <Select
                                                label="Value"
                                                name="value"
                                                value={ruleStatementData['value']}
                                                onChange={(e) => handleChangeInputRuleStatementData(e, ruleStatementData.id)}
                                            >
                                                {ruleStatementData['valueOptions'].map((valueOption, key) => (
                                                    <MenuItem key={key} value={valueOption.name ? valueOption.name : valueOption.devtype}>
                                                        {valueOption.name ? valueOption.name : valueOption.devtype}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            sx={{ width: '100%' }}
                                            label="Value"
                                            name="value"
                                            value={ruleStatementData['value']}
                                            inputProps={{
                                                maxLength: 10
                                            }}
                                            onChange={(e) => handleChangeInputRuleStatementData(e, ruleStatementData.id)}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={1}>
                                    {/*<Select*/}
                                    {/*    name="connector"*/}
                                    {/*    value={ruleStatementData['connector']}*/}
                                    {/*    disabled={ruleStatementData['disabledConnector']}*/}
                                    {/*    onChange={(e) => handleChangeInputRuleStatementData(e, ruleStatementData.id)}*/}
                                    {/*>*/}
                                    {/*    {data.connectors.map((connectorData, key) => (*/}
                                    {/*        <MenuItem key={key} value={connectorData.connectorid}>(*/}
                                    {/*            {connectorData.name}*/}
                                    {/*        </MenuItem>*/}
                                    {/*    ))}*/}
                                    {/*</Select>*/}
                                </Grid>
                            </React.Fragment>
                        ))}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                {/*<Button variant="contained" startIcon={<Add />} onClick={handleAddClause} disabled={disableAddClause}>*/}
                {/*    Add Clause*/}
                {/*</Button>*/}
                <Button onClick={onCloseCancel}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={disableAddClause}>
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AddEnergyRuleDialog.propTypes = {
    onClose: PropTypes.func,
    onCloseCancel: PropTypes.func,
    ruleType: PropTypes.string,
    data: PropTypes.object,
    ruleStatementValues: PropTypes.object
};

export default AddEnergyRuleDialog;
